import React from 'react';

export default function ActivityLog({ logs, onRefresh }) {
  const getEventIcon = (eventType) => {
    const icons = {
      'profile_update': '✏️',
      'email_change_requested': '📧',
      'password_changed': '🔒',
      'login': '🔑',
      'logout': '🚪',
      'two_factor_enabled': '🔐',
      'two_factor_disabled': '🔓',
      'avatar_updated': '🖼️',
      'workspace_updated': '🏢'
    };
    return icons?.[eventType] || '📝';
  };

  const getEventColor = (eventType) => {
    if (eventType?.includes('password') || eventType?.includes('two_factor')) {
      return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    }
    if (eventType?.includes('login') || eventType?.includes('logout')) {
      return 'bg-blue-50 border-blue-200 text-blue-800';
    }
    if (eventType?.includes('email')) {
      return 'bg-purple-50 border-purple-200 text-purple-800';
    }
    return 'bg-gray-50 border-gray-200 text-gray-800';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Activity Log</h2>
          <p className="text-sm text-gray-500 mt-1">Recent account activity and security events</p>
        </div>
        <button
          onClick={onRefresh}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          🔄 Refresh
        </button>
      </div>

      {logs?.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Yet</h3>
          <p className="text-sm text-gray-500">Your account activity will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs?.map((log) => (
            <div
              key={log?.id}
              className={`border rounded-lg p-4 transition-all hover:shadow-md ${getEventColor(log?.eventType)}`}
            >
              <div className="flex items-start space-x-4">
                <div className="text-2xl flex-shrink-0">
                  {getEventIcon(log?.eventType)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {log?.eventDescription}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                        <span className="flex items-center">
                          🕐 {formatDate(log?.createdAt)}
                        </span>
                        {log?.ipAddress && (
                          <span className="flex items-center">
                            🌐 {log?.ipAddress}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white bg-opacity-50">
                      {log?.eventType?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {log?.userAgent && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
                        View device details
                      </summary>
                      <p className="text-xs text-gray-600 mt-1 p-2 bg-white bg-opacity-50 rounded break-all">
                        {log?.userAgent}
                      </p>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {logs?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="text-blue-600 mr-3">ℹ️</div>
            <div>
              <h4 className="text-sm font-medium text-blue-900">About Activity Logs</h4>
              <p className="text-xs text-blue-700 mt-1">
                Activity logs help you monitor your account security. Review them regularly to ensure all actions were authorized by you.
                If you notice any suspicious activity, change your password immediately and contact support.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}